import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LocaleService } from '@selfcare/cms';
import { TranslatePipe } from '@core/pipes';
import { AsyncPipe, CommonModule } from '@angular/common';
import { NavigationStart, Router } from '@angular/router';
import { filter, tap } from 'rxjs/operators';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { AppRouting } from '../../../core/enums';
import { MsisdnRadioButtonListComponent } from '../msisdn-radio-button-list/msisdn-radio-button-list.component';
import { OptionComponent } from '../option/option.component';
import { SelectComponent } from '../select/select.component';
import { Store } from '@ngrx/store';
import { UserActions, userFeature } from '@state';
import { RemoveTermShorthandPipe } from '@core/pipes/remove-term-shorthand.pipe';
import { SvgComponent } from '../svg/svg.component';
import { ButtonComponent } from '../button/button.component';
import { ClickToCopyDirective } from '@core/directives';
import { ACTIVE_PRODUCT_LOCALSTORAGE_KEY, CURRENT_MSISDN_LOCALSTORAGE_KEY } from '@core/const/const';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { environment } from '@src/environments/environment';
import { ProfileStateProvider } from '@core/services';
import { ProductType } from '@core/contracts/data-contracts';
import { getProductConfig } from '@core/helpers';

@UntilDestroy()
@Component({
  selector: 'sc-mini-profile',
  imports: [
    SvgComponent,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    AsyncPipe,
    MsisdnRadioButtonListComponent,
    OptionComponent,
    SelectComponent,
    CommonModule,
    RemoveTermShorthandPipe,
    ButtonComponent,
    ClickToCopyDirective,
  ],
  templateUrl: './mini-profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MiniProfileComponent implements OnInit {
  @ViewChild('scrollContainer') scrollContainerRef!: ElementRef<HTMLElement>;
  @ViewChild(MsisdnRadioButtonListComponent) msisdnListCmp!: MsisdnRadioButtonListComponent;

  public readonly productType = ProductType;

  public readonly mainUrl = `${environment.mainUrl}/place-order`;

  public localeList$ = this.localeService.locales$;

  public form = this.fb.group<{ languageCode: string }>({
    languageCode: this.localeService.currentLocale,
  });

  public isOpen = signal(false);
  public user = this.store.selectSignal(userFeature.selectCurrent);
  public userNameDetails = this.store.selectSignal(userFeature.selectUserNameDetails);
  public userInitials = this.store.selectSignal(userFeature.selectUserInitials);
  public isLimited = this.store.selectSignal(userFeature.selectIsMsisdnLimited);
  public isB2B = this.store.selectSignal(userFeature.selectIsB2B);
  public activeProduct = this.store.selectSignal(userFeature.selectActiveProduct);
  public products = this.store.selectSignal(userFeature.selectProducts);

  public isProductSwitchAvailable = computed(() => this.products().length > 1);

  readonly fullName = computed(() => {
    const details = this.userNameDetails();

    return details.prefix
      ? `${details.firstName} ${details.prefix} ${details.lastName}`
      : `${details.firstName} ${details.lastName}`;
  });

  public readonly productConfig = computed(() => getProductConfig(this.activeProduct()));

  public selectedLanguage = signal<string>(this.localeService.currentLocale);

  constructor(
    private readonly fb: FormBuilder,
    private readonly localeService: LocaleService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly authenticationService: AuthenticationService,
    private readonly profileStateProvider: ProfileStateProvider
  ) { }

  ngOnInit() {
    this.form.valueChanges
      .pipe(
        untilDestroyed(this),
        tap((value) => this.localeService.changeLocale(value.languageCode))
      )
      .subscribe();

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter((e) => e instanceof NavigationStart),
        tap(() => this.isOpen.set(false))
      )
      .subscribe();
  }

  toggle() {
    const newState = !this.isOpen();
    this.isOpen.set(newState);
    this.profileStateProvider.isProfileOpen.set(newState);
    if (this.isOpen()) {
      setTimeout(() => {
        this.msisdnListCmp?.scrollToSelectedMsisdn();
      });
    }
  }

  public goToSettings() {
    this.isOpen.set(false);
    this.router.navigate([AppRouting.Settings]);
  }

  close() {
    this.isOpen.set(false);
    this.profileStateProvider.isProfileOpen.set(false);
  }

  signout() {
    localStorage.removeItem(CURRENT_MSISDN_LOCALSTORAGE_KEY);
    localStorage.removeItem(ACTIVE_PRODUCT_LOCALSTORAGE_KEY);
    this.authenticationService.logout();
    this.router.navigate([AppRouting.Login]);
  }

  public outSelectedOption(lang: string) {
    this.selectedLanguage.set(lang);
  }

  public setActiveProduct(selectedProduct: ProductType) {
    this.store.dispatch(UserActions.setActiveProduct(selectedProduct));
    this.router.navigateByUrl(this.router.url, { onSameUrlNavigation: 'reload' });
  }
}
